import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import {
  mergeReducedMotionPreset,
  resolveExpandTransition,
  useThemeMotion,
} from '../../animation/themeMotion'
import { useTheme } from '../../theme/ThemeContext'
import type { AgentInlineOption } from './AgentInlinePicker'

type AgentSkillSlashMenuProps = {
  /** 是否打开：输入「/」后才为 true（宿主由输入内容派生），避免菜单常驻 */
  open: boolean
  /** 已过滤的技能选项（value = publicId；过滤逻辑在宿主输入坞，便于键盘导航共用） */
  options: AgentInlineOption[]
  /** 已引用的技能 value 集合（菜单内打勾，可重复点击切换） */
  selectedValues: string[]
  /** 当前高亮项下标（受控，宿主负责键盘导航） */
  highlightIndex: number
  /** 无匹配时的提示文案（技能未配置 / 过滤无结果） */
  emptyHint: string
  onSelect: (option: AgentInlineOption) => void
  /** 菜单锚点：输入框容器元素 */
  anchorRef: React.RefObject<HTMLElement | null>
}

type MenuLayout = { bottom: number; left: number; width: number; maxHeight: number }

const VIEWPORT_MARGIN = 8
const GAP = 8

/**
 * 智能体技能斜杠引用菜单：输入框键入「/」触发，回车/点选引用技能。
 * 菜单悬浮在输入框上方（portal 定位），与 AgentInlinePicker 同一套视觉语言；
 * 过滤与高亮由宿主（AgentComposerDock）控制，键盘导航和提交共用一套状态。
 */
export function AgentSkillSlashMenu({
  open,
  options,
  selectedValues,
  highlightIndex,
  emptyHint,
  onSelect,
  anchorRef,
}: AgentSkillSlashMenuProps) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const motionPreset = useThemeMotion()
  const reducedMotion = useReducedMotion()
  const dropdownTrans = resolveExpandTransition(
    mergeReducedMotionPreset(motionPreset, !!reducedMotion),
    { reducedMotion: !!reducedMotion },
  )
  const [layout, setLayout] = useState<MenuLayout | null>(null)

  useLayoutEffect(() => {
    const el = anchorRef.current
    if (!el) return
    const sync = () => {
      const rect = el.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight
      // 宽度与编辑框一致（响应式跟随锚点，超窄屏夹到视口内）
      const width = Math.min(rect.width, vw - 2 * VIEWPORT_MARGIN)
      const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN
      setLayout({
        bottom: vh - rect.top + GAP,
        left: Math.max(VIEWPORT_MARGIN, Math.min(rect.left, vw - VIEWPORT_MARGIN - width)),
        width,
        maxHeight: Math.min(300, Math.max(140, spaceAbove)),
      })
    }
    sync()
    window.addEventListener('resize', sync)
    window.addEventListener('scroll', sync, true)
    return () => {
      window.removeEventListener('resize', sync)
      window.removeEventListener('scroll', sync, true)
    }
  }, [anchorRef])

  // 未键入「/」时绝不渲染：菜单只由输入内容驱动出现/消失
  if (!open || !layout) return null

  return typeof document !== 'undefined'
    ? createPortal(
        <AnimatePresence>
          <motion.div
            role="listbox"
            aria-label="引用技能"
            className={[
              'overflow-hidden bg-[color-mix(in_srgb,var(--menu-solid)_96%,transparent)] shadow-float ring-1 ring-[color-mix(in_srgb,var(--line)_35%,transparent)]',
              angular ? 'rounded-none' : 'rounded-2xl',
            ].join(' ')}
            style={{
              position: 'fixed',
              bottom: layout.bottom,
              left: layout.left,
              width: layout.width,
              maxHeight: layout.maxHeight,
              zIndex: 120000,
            }}
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6, scale: 0.99 }}
            transition={dropdownTrans}
          >
            {options.length === 0 ? (
              <p className="px-3.5 py-4 text-center text-xs leading-5 text-ink-soft">{emptyHint}</p>
            ) : (
              <>
                <div className="px-3 py-2.5">
                  <p className="text-[11px] font-medium tracking-[0.08em] text-ink-soft">
                    引用技能（输入过滤，回车选中）
                  </p>
                </div>
                <div className="scroll-region max-h-[15rem] overflow-y-auto p-1.5">
                  {options.map((option, index) => {
                const active = selectedValues.includes(option.value)
                const highlighted = index === highlightIndex
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={[
                      'flex w-full items-center gap-3 px-3 py-2.5 text-left transition',
                      angular ? 'rounded-none' : 'rounded-xl',
                      highlighted
                        ? 'bg-[var(--segment-active-bg)] text-[var(--segment-active-fg)]'
                        : 'text-ink hover:bg-surface-muted/80',
                    ].join(' ')}
                    onMouseEnter={() => undefined}
                    onClick={() => onSelect(option)}
                  >
                    <span
                      aria-hidden
                      className={[
                        'flex h-4 w-4 shrink-0 items-center justify-center border',
                        angular ? 'rounded-none' : 'rounded-[5px]',
                        active
                          ? 'border-[var(--accent)] bg-[var(--accent)] text-white'
                          : 'border-[color-mix(in_srgb,var(--line)_60%,transparent)] bg-transparent',
                      ].join(' ')}
                    >
                      {active ? (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      ) : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{option.label}</span>
                      {option.description ? (
                        <span
                          className={[
                            'mt-0.5 block truncate text-xs',
                            highlighted ? 'opacity-80' : 'text-ink-soft',
                          ].join(' ')}
                        >
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                    <span className="shrink-0 text-[11px] text-ink-soft">/</span>
                  </button>
                )
              })}
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>,
        document.body,
      )
    : null
}
